/**
 * Modelo de IA por etapa — o que o cliente pode trocar.
 *
 * Tela separada de `Settings`, que é do time UpOwl e é de leitura. Aqui o
 * cliente escreve, então a rota passa pela área `configuracoes.llm`, que exige
 * a capability `admin.llm.configure` (só a persona `owner` recebe).
 *
 * Decisões de layout, todas vindas da primeira versão:
 *
 * - Cada etapa é uma linha inteira, com o seletor ABAIXO da explicação. Na
 *   versão anterior o seletor dividia a largura com o texto e espremia a
 *   explicação em quatro palavras por linha.
 * - Preço, janela e dona aparecem no cartão do modelo escolhido, não só dentro
 *   do menu — o cliente precisa ver o que está usando sem abrir nada.
 * - Os modelos que NÃO servem aparecem, apagados e com o motivo. "Por que o
 *   DeepSeek não está aqui?" é a primeira pergunta; esconder gera chamado.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Chip, Divider, MenuItem, Paper, Select, Skeleton,
  Stack, Tooltip, Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PageHeader } from "../components/PageHeader";
import { ChavesDeApi, type Chaves } from "../components/ChavesDeApi";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../context/TenantContext";
import { AvisoDeLeitura } from "../components/AvisoDeLeitura";

type Modelo = {
  nome: string; rotulo: string; provedor: string; dona: string; nota?: string;
  preco_entrada: number; preco_saida: number; contexto: number;
  saida_maxima: number; esforcos: string[];
  // Áudio é cobrado por minuto, não por token; zero significa "não se aplica".
  preco_por_minuto: number;
};
type Recusado = Modelo & { falta: string[] };
type EtapaCliente = {
  chave: string; rotulo: string; porque: string; padrao: string;
  escolhido: string; exige: string[]; opcoes: Modelo[]; recusados: Recusado[];
  // De quem é a chave que o padrão precisa, e se o cliente já a tem. O padrão
  // escolhe o modelo; a credencial continua sendo dele.
  padrao_dona: string; padrao_tem_chave: boolean;
};
type Resposta = {
  etapas: EtapaCliente[]; chaves: Chaves; precos_conferidos_em: string;
};

/** Cor da dona — para o olho separar provedores sem ler. */
const COR_DONA: Record<string, string> = {
  openai: "#10a37f",
  anthropic: "#d97757",
  deepseek: "#4d6bfe",
  openrouter: "#8b5cf6",
  vercel: "#111827",
};

const FALTA_EM_PORTUGUES: Record<string, string> = {
  ferramentas: "não usa ferramentas",
  estado_no_servidor: "não guarda a conversa no servidor",
  esforco: "não aceita ajuste de esforço",
  saida_estruturada: "não devolve resposta estruturada",
  audio_entrada: "não transcreve áudio",
  audio_saida: "não gera áudio",
  // Este é o único da lista que o cliente resolve sozinho, e por isso é o
  // único escrito como instrução em vez de como defeito do modelo.
  chave_do_provedor: "falta cadastrar a sua chave desta empresa",
};

function dinheiro(v: number) {
  if (!v) return "—";
  return v < 1 ? `$${v.toFixed(2)}` : `$${v.toFixed(2).replace(/\.00$/, "")}`;
}

function compacto(n: number) {
  if (!n) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

function SeloDona({ m }: { m: { dona: string; provedor: string } }) {
  const cor = COR_DONA[m.provedor] ?? "#64748b";
  return (
    <Chip
      size="small" label={m.dona}
      sx={{
        height: 20, fontSize: 11, fontWeight: 600, color: cor,
        bgcolor: `${cor}14`, border: `1px solid ${cor}33`,
      }}
    />
  );
}

/** Preço, janela e saída — a linha que responde "quanto custa isso". */
function Numeros({ m }: { m: Modelo }) {
  const item = (rotulo: string, valor: string, dica: string) => (
    <Tooltip title={dica} arrow>
      <Box sx={{ minWidth: 92 }}>
        <Typography sx={{ fontSize: 10.5, color: "text.secondary", letterSpacing: .3 }}>
          {rotulo}
        </Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontFamily: "ui-monospace, monospace" }}>
          {valor}
        </Typography>
      </Box>
    </Tooltip>
  );
  return (
    <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap>
      {item("ENTRADA", dinheiro(m.preco_entrada), "Dólares por milhão de tokens enviados ao modelo")}
      {item("SAÍDA", dinheiro(m.preco_saida), "Dólares por milhão de tokens gerados. Costuma ser o que mais pesa.")}
      {item("JANELA", compacto(m.contexto), "Quanto cabe numa conversa: pergunta, histórico, dados e resposta")}
      {item("SAÍDA MÁX.", compacto(m.saida_maxima), "Tamanho máximo de uma resposta")}
    </Stack>
  );
}

export function ModeloDeIA() {
  const { tenant } = useTenant();
  const api = useApi();
  const qc = useQueryClient();
  const [rascunho, setRascunho] = useState<Record<string, string>>({});
  const [erros, setErros] = useState<string[]>([]);
  const [salvo, setSalvo] = useState(false);

  const q = useQuery({
    queryKey: ["settings-llm", tenant],
    queryFn: () => api.call<Resposta>("/api/settings/llm"),
    enabled: !!tenant,
  });

  const salvar = useMutation({
    mutationFn: (corpo: Record<string, string>) =>
      // `useApi` não define Content-Type; sem ele o Flask não lê o corpo
      // (`request.get_json` devolve None) e o PUT volta 400 sem motivo claro.
      api.call<Resposta>("/api/settings/llm", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(corpo),
      }),
    onSuccess: () => {
      setErros([]); setSalvo(true); setRascunho({});
      qc.invalidateQueries({ queryKey: ["settings-llm", tenant] });
    },
    onError: (e: unknown) => {
      setSalvo(false);
      const d = (e as { detalhes?: string[] })?.detalhes;
      setErros(d?.length ? d : [(e as Error)?.message || "Não consegui salvar."]);
    },
  });

  const dados = q.data;
  const valorDe = (e: EtapaCliente) => rascunho[e.chave] ?? e.escolhido ?? "";
  const mudou = useMemo(() => Object.keys(rascunho).length > 0, [rascunho]);

  return (
    <>
      {q.error && <AvisoDeLeitura oQue="a configuração de modelo" />}
      <PageHeader
        eyebrow="Configurações"
        title="Modelo de IA"
        subtitle="Escolha qual modelo a Sophia usa em cada etapa. Vale só para a sua empresa."
      />

      {erros.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErros([])}>
          {erros.map((m, i) => <div key={i}>{m}</div>)}
        </Alert>
      )}
      {salvo && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSalvo(false)}>
          Salvo. Vale a partir da próxima mensagem — não precisa reiniciar nada.
        </Alert>
      )}

      {q.isLoading && <Skeleton variant="rounded" height={180} sx={{ mb: 2 }} />}

      {/* A chave vem antes da escolha, na tela como no servidor: sem
          credencial não há modelo para selecionar, e uma lista vazia sem
          explicação é o jeito mais rápido de gerar um chamado. */}
      {dados?.chaves && <ChavesDeApi chaves={dados.chaves} />}

      <Stack spacing={2}>
        {dados?.etapas.map((e) => {
          const atual = e.opcoes.find((o) => o.nome === valorDe(e));
          const alterada = rascunho[e.chave] !== undefined;
          return (
            <Paper
              key={e.chave} variant="outlined"
              sx={{
                p: 2.5, borderRadius: 2,
                borderColor: alterada ? "warning.main" : "divider",
                bgcolor: alterada ? "warning.50" : "background.paper",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {e.rotulo}
              </Typography>
              {/* largura de leitura: a explicação é o que faz a escolha ser informada */}
              <Typography
                variant="body2" color="text.secondary"
                sx={{ mt: 0.5, mb: 2, maxWidth: 860, lineHeight: 1.6 }}
              >
                {e.porque}
              </Typography>

              <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ lg: "center" }}>
                <Select
                  size="small" displayEmpty value={valorDe(e)}
                  sx={{ minWidth: 340, bgcolor: "background.paper" }}
                  onChange={(ev) => {
                    setSalvo(false);
                    setRascunho((r) => ({ ...r, [e.chave]: ev.target.value as string }));
                  }}
                >
                  <MenuItem value="">
                    <em>Padrão da UpOwl — {e.padrao} (chave {e.padrao_dona})</em>
                  </MenuItem>
                  {e.opcoes.map((o) => (
                    <MenuItem key={o.nome} value={o.nome}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <SeloDona m={o} />
                        <span>{o.rotulo}</span>
                        <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                          {o.preco_por_minuto
                            ? `US$ ${o.preco_por_minuto.toFixed(3)}/min`
                            : `${dinheiro(o.preco_entrada)} / ${dinheiro(o.preco_saida)}`}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>

                {atual ? (
                  <Box sx={{ flex: 1 }}>
                    <Numeros m={atual} />
                    {atual.nota && (
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.75 }}>
                        {atual.nota}
                      </Typography>
                    )}
                  </Box>
                ) : e.padrao_tem_chave ? (
                  <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                    Usando o padrão da UpOwl (<strong>{e.padrao}</strong>), cobrado
                    na sua chave da {e.padrao_dona}.
                  </Typography>
                ) : (
                  // O caso que o texto antigo escondia: o padrão está definido,
                  // parece resolvido, e mesmo assim esta etapa não roda.
                  <Typography sx={{ fontSize: 12.5, color: "warning.dark", fontWeight: 600 }}>
                    Esta etapa não vai rodar: o padrão da UpOwl é {e.padrao}, e falta
                    cadastrar a sua chave da {e.padrao_dona} acima.
                  </Typography>
                )}
              </Stack>

              {!!e.recusados?.length && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 1 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 14, verticalAlign: "-2px", mr: .5 }} />
                    Não servem para esta etapa
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {e.recusados.map((r) => (
                      <Tooltip
                        key={r.nome} arrow
                        title={`${r.rotulo}: ${r.falta.map((f) => FALTA_EM_PORTUGUES[f] ?? f).join("; ")}`}
                      >
                        <Chip
                          size="small" variant="outlined" label={`${r.dona} · ${r.rotulo}`}
                          sx={{ fontSize: 11, opacity: .55 }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </>
              )}
            </Paper>
          );
        })}
      </Stack>

      {mudou && (
        <Paper
          elevation={3}
          sx={{
            position: "sticky", bottom: 16, mt: 2, p: 1.5, borderRadius: 2,
            display: "flex", gap: 1, alignItems: "center",
          }}
        >
          <Typography sx={{ flex: 1, fontSize: 13 }}>
            {Object.keys(rascunho).length} etapa(s) alterada(s), ainda não salvas.
          </Typography>
          <Button onClick={() => { setRascunho({}); setErros([]); }}>Descartar</Button>
          <Button variant="contained" disabled={salvar.isPending}
                  onClick={() => salvar.mutate(rascunho)}>
            {salvar.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </Paper>
      )}

      {dados?.precos_conferidos_em && (
        <Typography sx={{ mt: 3, fontSize: 11.5, color: "text.secondary" }}>
          Preços por milhão de tokens, em dólar, conferidos em {dados.precos_conferidos_em}.
          Servem para comparar ordens de grandeza — há desconto por cache e por volume,
          e alguns provedores variam por horário.
        </Typography>
      )}
    </>
  );
}
